/* eslint-disable unicorn/no-thenable, unicorn/no-useless-undefined */
import { RuleTypes, VisibilityTypes } from '@src/api';
import { REQUIRED_FIELD } from '@src/utils';
import * as yup from 'yup';

const optionalRules = {
  then: schemalocal => schemalocal.required(REQUIRED_FIELD),
  otherwise: schemalocal => schemalocal,
};

const criteriaRuleTypeField = 'criteriaRuleType';

const isResultRule = val =>
  val === RuleTypes.MissingResultRule || val === RuleTypes.ResultRule || val === RuleTypes.ResultRefValueRule;

const resultRepeatFields = () => {
  return {
    scaleType: yup.string().when(criteriaRuleTypeField, {
      is: val => isResultRule(val) || val === RuleTypes.ConditionAndDiagnosisRule,
      ...optionalRules,
    }),
    scaleValue: yup.string().when(criteriaRuleTypeField, {
      is: val => isResultRule(val) || val === RuleTypes.ConditionAndDiagnosisRule,
      ...optionalRules,
    }),
    loincItemId: yup.string().when(criteriaRuleTypeField, {
      is: isResultRule,
      ...optionalRules,
    }),
  };
};

export const schema = yup.object({
  name: yup.string().max(255).required(REQUIRED_FIELD),
  referenceLink: yup.string().required(REQUIRED_FIELD),
  internalDescription: yup.string(),
  patientDescription: yup.string(),
  visibilityType: yup.string(),
  createVisibility: yup
    .object({
      salesRepresentativeId: yup
        .string()
        .nullable()
        .test('requiredField', REQUIRED_FIELD, function (value) {
          const { from } = this;
          return from?.[1]?.value?.visibilityType === VisibilityTypes.ForOneSalesRepresentative ? !!value : true;
        }),
      laboratoryIntersectionType: yup.string(),
      laboratoryIds: yup.array().of(yup.string().required()),
      facilityIntersectionType: yup.string(),
      facilityVisibilities: yup.array().of(
        yup
          .object({
            facilityId: yup.string(),
            orderingProviderIds: yup.array().of(yup.string()),
          })
          .default([]),
      ),
    })
    .default(null)
    .nullable(),
  criterias: yup
    .array()
    .of(
      yup.object().shape(
        {
          criteriaRuleType: yup.string().required(REQUIRED_FIELD),
          nextOperation: yup.string().optional().nullable(),

          // Patient age fields
          age: yup.string().when(criteriaRuleTypeField, {
            is: RuleTypes.PatientAgeRule,
            ...optionalRules,
          }),
          compareOperator: yup.string().when(criteriaRuleTypeField, {
            is: RuleTypes.PatientAgeRule,
            ...optionalRules,
          }),

          // Patient birth sex fields
          birthSexes: yup
            .array()
            .of(yup.string())
            .default(undefined)
            .when(criteriaRuleTypeField, {
              is: RuleTypes.PatientBirthSexRule,
              then: schemalocal => schemalocal.min(1),
              otherwise: schemalocal => schemalocal.optional(),
            }),

          // Result Rule fields
          loincUnitId: yup.string().optional().nullable(),

          singleValue: yup
            .object()
            .shape({
              compareOperator: yup.string().required(REQUIRED_FIELD),
              value: yup.string().required(REQUIRED_FIELD),
            })
            .nullable()
            .default(undefined)
            .when([criteriaRuleTypeField, 'rangeValues'], {
              is: (ruleType, rangeValues) => ruleType === RuleTypes.ResultRule && !rangeValues,
              ...optionalRules,
            }),
          rangeValues: yup
            .object()
            .shape({
              from: yup.string().required(REQUIRED_FIELD),
              to: yup.string().required(REQUIRED_FIELD),
            })
            .nullable()
            .default(undefined)
            .when([criteriaRuleTypeField, 'singleValue'], {
              is: (ruleType, singleValue) => ruleType === RuleTypes.ResultRule && !singleValue,
              ...optionalRules,
            }),

          // Result Ref fields
          resultFlags: yup
            .array()
            .of(yup.string().required(REQUIRED_FIELD))
            .default(undefined)
            .when(criteriaRuleTypeField, {
              is: RuleTypes.ResultRefValueRule,
              then: schemalocal => schemalocal.min(1),
              otherwise: schemalocal => schemalocal.optional(),
            }),
          ...resultRepeatFields(),
          // Diagnoses fields

          diagnosisIds: yup
            .array()
            .of(yup.string())
            .nullable()
            .default(undefined)
            .when([criteriaRuleTypeField], {
              is: ruleType => ruleType === RuleTypes.ConditionAndDiagnosisRule,
              then: schemalocal => schemalocal.min(1).required().nullable(),
              otherwise: schemalocal => schemalocal,
            })
            .test(function (value) {
              const { diagnosisGroupIds } = this?.parent;
              if (diagnosisGroupIds === null) return value != null;
              return true;
            }),
          diagnosisGroupIds: yup
            .array()
            .of(yup.string())
            .default(undefined)
            .nullable()
            .when([criteriaRuleTypeField], {
              is: ruleType => ruleType === RuleTypes.ConditionAndDiagnosisRule,
              then: schemalocal => schemalocal.optional().nullable(),
              otherwise: schemalocal => schemalocal,
            })
            .test(function (value) {
              const { diagnosisIds } = this?.parent;
              if (diagnosisIds === null) return value != null;
              return true;
            }),
        },
        [['rangeValues', 'singleValue']],
      ),
    )
    .nullable(),
});
